import { RobotOutlined } from "@ant-design/icons";
import { Avatar, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { AuthorTypeEnum } from "@fitness-app/data-models/entities/AuthorTypeEnum";
import { type ClientNoteWithAuthorData } from "@fitness-app/data-models/entities/ClientNote";
import { getAuthorName } from "@fitness-app/data-models/utils/getAuthorName";
import { getUserInitials } from "@fitness-app/utils";

type NoteAuthorData = Pick<ClientNoteWithAuthorData, "author" | "authorType">;

/** Notes written by automation carry no author; they are not notes of a deleted account (TRE-2217). */
const AUTOMATED_AUTHOR_TYPES: string[] = [AuthorTypeEnum.System, AuthorTypeEnum.Agent];

const AUTHOR_TYPE_TAG: Partial<Record<string, { color: string; key: string; fallback: string }>> = {
  [AuthorTypeEnum.System]: { color: "cyan", key: "notes.authorType.system", fallback: "System" },
  [AuthorTypeEnum.Agent]: { color: "gold", key: "notes.authorType.agent", fallback: "Opiekunka" },
  [AuthorTypeEnum.Sales]: { color: "purple", key: "notes.authorType.sales", fallback: "Sprzedaż" },
};

export const NoteAuthorAvatar = ({ note }: { note: NoteAuthorData }) => {
  const { t } = useTranslation("trainees");

  if (note.author) {
    return (
      <Tooltip title={getAuthorName(note.author)}>
        <Avatar size="small" src={note.author.avatarUrl}>
          {getUserInitials(note.author)}
        </Avatar>
      </Tooltip>
    );
  }

  if (AUTOMATED_AUTHOR_TYPES.includes(note.authorType)) {
    const tag = AUTHOR_TYPE_TAG[note.authorType]!;

    return (
      <Tooltip title={t(tag.key, tag.fallback)}>
        <Avatar size="small" icon={<RobotOutlined />} />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("notes.authorDeleted")}>
      <Avatar size="small">{t("notes.authorDeleted")}</Avatar>
    </Tooltip>
  );
};

export const NoteAuthorTypeTag = ({ note }: { note: Pick<ClientNoteWithAuthorData, "authorType"> }) => {
  const { t } = useTranslation("trainees");
  const tag = AUTHOR_TYPE_TAG[note.authorType];

  return tag ? <Tag color={tag.color}>{t(tag.key, tag.fallback)}</Tag> : null;
};
